import axios from "axios";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getStoreId,
  getWorkspaceId,
  hasSessionMarker,
  saveTokens,
} from "./session";

export const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://127.0.0.1:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 45000,
  headers: { Accept: "application/json" },
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: { Accept: "application/json" },
});

let refreshPromise = null;

function isAuthEndpoint(url = "") {
  return ["/auth/login", "/auth/register", "/auth/refresh", "/auth/forgot-password", "/auth/reset-password"].some((path) =>
    url.includes(path),
  );
}

function notifyExpired() {
  window.dispatchEvent(new CustomEvent("auth:expired"));
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    const payload = refreshToken ? { refresh_token: refreshToken } : undefined;

    refreshPromise = refreshClient
      .post("/auth/refresh", payload)
      .then((response) => {
        saveTokens(response.data);
        return response.data.access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  const workspaceId = getWorkspaceId();
  const storeId = getStoreId();

  config.headers = config.headers || {};

  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (workspaceId) config.headers["X-Workspace-Id"] = workspaceId;
  if (storeId) config.headers["X-Store-Id"] = storeId;

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 428 && detail?.code === "legal_acceptance_required") {
      window.dispatchEvent(new CustomEvent("legal:required", { detail }));
      return Promise.reject(error);
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    if (!hasSessionMarker() && !getRefreshToken()) {
      clearTokens();
      notifyExpired();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api(originalRequest);
    } catch (refreshError) {
      clearTokens();
      notifyExpired();
      return Promise.reject(refreshError);
    }
  },
);

export default api;
